<div class="min-h-full bg-transparent px-4 xl:px-6 py-6 space-y-6" id="deck_admin_page">

  <!-- Toolbar (the page title + product badge live in the tab shell) -->
  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
    <p class="text-sm text-slate-500 dark:text-slate-400">
      Categories of HTML slides. The AI opens a category and walks its slides.
    </p>
    <div class="flex items-center gap-2 flex-wrap">
      <label class="flex items-center gap-2 cursor-pointer select-none">
        <button
          type="button"
          (click)="toggleIncludeInactive()"
          class="relative w-9 h-5 rounded-full transition-colors duration-200"
          [class.bg-indigo-500]="includeInactive()"
          [class.bg-slate-200]="!includeInactive()"
          [class.dark:bg-slate-700]="!includeInactive()"
          id="deck_admin_include_inactive_toggle"
        >
          <span
            class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200"
            [class.translate-x-4]="includeInactive()"
            [class.translate-x-0]="!includeInactive()"
          ></span>
        </button>
        <span class="text-xs font-medium text-slate-500 dark:text-slate-400">Show inactive</span>
      </label>

      <button
        (click)="loadGroups()"
        [disabled]="loading()"
        class="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700
               text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50
               transition-colors disabled:opacity-50"
        id="deck_admin_refresh_btn"
      >
        <svg class="w-3.5 h-3.5" [class.animate-spin]="loading()"
             viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="23 4 23 10 17 10"></polyline>
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
        </svg>
        Refresh
      </button>

      <button
        (click)="openCategoryPanel('create', null)"
        class="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white
               bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700
               shadow-sm shadow-purple-500/25 transition-all active:scale-95"
        id="deck_admin_add_category_btn"
      >
        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        New Category
      </button>
    </div>
  </div>

  <!-- Stats bar -->
  <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
    <div class="bg-white dark:bg-surface-2/60 rounded-xl border border-slate-100 dark:border-slate-700/50 px-4 py-3">
      <p class="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Categories</p>
      <p class="text-2xl font-bold text-slate-800 dark:text-white mt-0.5">{{ deckMeta()?.count ?? '—' }}</p>
    </div>
    <div class="bg-white dark:bg-surface-2/60 rounded-xl border border-slate-100 dark:border-slate-700/50 px-4 py-3">
      <p class="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Slides</p>
      <p class="text-2xl font-bold text-emerald-500 mt-0.5">{{ totalPages() }}</p>
    </div>
    <div class="bg-white dark:bg-surface-2/60 rounded-xl border border-slate-100 dark:border-slate-700/50 px-4 py-3">
      <p class="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Deck</p>
      <p class="text-base font-bold text-purple-500 mt-0.5 truncate">{{ deckMeta()?.deck ?? '—' }}</p>
    </div>
    <div class="bg-white dark:bg-surface-2/60 rounded-xl border border-slate-100 dark:border-slate-700/50 px-4 py-3">
      <p class="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Opens the call</p>
      <p class="text-sm font-bold mt-1 truncate"
         [class.text-indigo-500]="introGroup()"
         [class.text-amber-500]="!introGroup()">
        {{ introGroup()?.title ?? 'Not set' }}
      </p>
    </div>
  </div>

  <!-- No intro warning -->
  <div
    *ngIf="!loading() && groups().length && !introGroup()"
    class="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 text-sm text-amber-800 dark:text-amber-400"
    id="deck_admin_no_intro_warning"
  >
    No category is marked <strong>“Opens the call”</strong>. Until one is, a call with no
    matching industry opens on whichever category sorts first — which is how a
    chiropractic case study ended up in front of an orthodontist. Edit a category to fix it.
  </div>

  <!-- Error state -->
  <div
    *ngIf="errorMsg()"
    class="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-sm text-red-700 dark:text-red-400"
  >
    {{ errorMsg() }}
  </div>

  <!-- Loading skeleton -->
  <div *ngIf="loading() && !groups().length" class="space-y-2">
    <div *ngFor="let i of [1,2,3,4,5]" class="h-14 rounded-xl bg-slate-100 dark:bg-slate-800/50 animate-pulse"></div>
  </div>

  <!-- Two panes -->
  <div *ngIf="!loading() || groups().length" class="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-5 items-start">

    <!-- ── Left: categories ────────────────────────────────────────── -->
    <div class="bg-white dark:bg-surface-2/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 overflow-hidden shadow-sm">
      <div class="px-4 py-3 border-b border-slate-100 dark:border-slate-700/50">
        <p class="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Categories</p>
      </div>

      <p *ngIf="!groups().length" class="py-12 text-center text-slate-400 dark:text-slate-500 text-sm">
        No categories yet.
      </p>

      <ul id="deck_admin_category_list">
        <li
          *ngFor="let g of paginatedGroups(); trackBy: trackByGroup"
          (click)="selectGroup(g)"
          class="px-4 py-3 border-b border-slate-50 dark:border-slate-700/30 cursor-pointer transition-colors group"
          [ngClass]="g.group === selectedGroupKey()
            ? 'bg-purple-50 dark:bg-purple-900/20'
            : 'hover:bg-slate-50 dark:hover:bg-slate-700/20'"
          [class.opacity-50]="g.isActive === false"
          [attr.id]="'deck_category_row_' + g.group"
        >
          <div class="flex items-start gap-2">
            <span class="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-slate-100 dark:bg-slate-700/60 text-[11px] font-bold text-slate-600 dark:text-slate-300 shrink-0 mt-0.5">
              {{ g.index }}
            </span>
            <div class="min-w-0 flex-1">
              <p class="font-semibold text-slate-800 dark:text-white text-sm leading-tight truncate">{{ g.title }}</p>
              <code class="text-[10px] text-purple-600 dark:text-purple-400 font-mono">{{ g.group }}</code>
              <div class="flex items-center gap-1.5 mt-1.5 flex-wrap">
                <span class="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700/60 text-slate-500 dark:text-slate-400">
                  {{ g.pageCount ?? 0 }} slide{{ g.pageCount === 1 ? '' : 's' }}
                </span>
                <span *ngIf="g.isIntro"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 font-semibold">
                  Opens the call
                </span>
                <span *ngIf="g.industry"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">
                  {{ g.industry }}
                </span>
                <span *ngIf="countryBadge(g.country)"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-400 font-medium"
                      [title]="'Country: ' + countryTooltip(g.country)"
                      [attr.id]="'deck_category_country_badge_' + g.group">
                  {{ countryBadge(g.country) }}
                </span>
                <select class="text-[10px] px-1.5 py-0.5 rounded font-semibold border-0 cursor-pointer
                               focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                        [ngClass]="envBadgeClass(g.forEnv)"
                        [ngModel]="g.forEnv"
                        (ngModelChange)="requestGroupEnvChange(g, $event)"
                        (click)="$event.stopPropagation()"
                        title="Where this category is visible — pick a new environment to move it and its slides"
                        [attr.id]="'deck_category_env_select_' + g.group">
                  <option *ngFor="let opt of envOptions" [ngValue]="opt.value">{{ opt.label }}</option>
                </select>
                <span *ngIf="g.pageCount === 0"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400"
                      title="A category with no slides is never offered to the AI">
                  empty
                </span>
                <span *ngFor="let agent of g.agents"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400"
                      [title]="'Assigned to agent: ' + agent.name">
                  {{ agent.name }}
                </span>
                <span *ngIf="!g.agents?.length"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400"
                      title="This category is assigned to NO agent — edit it to select agents">
                  no agent
                </span>
              </div>
            </div>
            <div class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button (click)="openCategoryPanel('edit', g); $event.stopPropagation()"
                      class="w-6 h-6 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-100 dark:hover:bg-white/10 transition-colors"
                      title="Edit category"
                      [attr.id]="'deck_category_edit_' + g.group">
                <svg class="w-3 h-3 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
              <button (click)="confirmDeleteGroup(g); $event.stopPropagation()"
                      class="w-6 h-6 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                      title="Delete category and its slides"
                      [attr.id]="'deck_category_delete_' + g.group">
                <svg class="w-3 h-3 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                </svg>
              </button>
            </div>
          </div>
        </li>
      </ul>

      <div class="p-3 border-t border-slate-100 dark:border-slate-700/50" *ngIf="groups().length > 0">
        <app-pagination
          [currentPage]="categoriesPager.page()"
          [totalItems]="groups().length"
          [pageSize]="categoriesPager.pageSize()"
          [pageSizes]="[5, 10, 15, 20]"
          [forceStack]="true"
          (pageChange)="categoriesPager.setPage($event)"
          (pageSizeChange)="categoriesPager.setPageSize($event)"
        ></app-pagination>
      </div>
    </div>

    <!-- ── Right: the selected category's slides ───────────────────── -->
    <div class="bg-white dark:bg-surface-2/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 overflow-hidden shadow-sm">
      <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700/50">
        <div class="min-w-0">
          <p class="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Slides</p>
          <p *ngIf="selectedGroup()" class="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate flex items-center gap-1.5 flex-wrap">
            <span>in <strong class="text-slate-700 dark:text-slate-200">{{ selectedGroup()!.title }}</strong></span>
            <span *ngIf="countryBadge(selectedGroup()!.country)"
                  class="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-400 font-medium shrink-0"
                  [title]="'Country: ' + countryTooltip(selectedGroup()!.country)">
              {{ countryBadge(selectedGroup()!.country) }}
            </span>
            <span class="truncate">— opens when: {{ selectedGroup()!.when }}</span>
          </p>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <button
            *ngIf="slideSelection.keys().size > 0"
            (click)="confirmDeleteSelectedSlides()"
            class="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white
                   bg-red-500 hover:bg-red-600 shadow-sm transition-all active:scale-95"
            id="deck_admin_delete_selected_btn"
          >
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
            </svg>
            Delete Selected ({{ slideSelection.keys().size }})
          </button>
          <button
            *ngIf="selectedGroup()"
            (click)="openSlidePanel('create', null)"
            class="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white
                   bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700
                   shadow-sm shadow-purple-500/25 transition-all active:scale-95"
            id="deck_admin_add_slides_btn"
          >
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Slides
          </button>
        </div>
      </div>

      <p *ngIf="!selectedGroup()" class="py-16 text-center text-slate-400 dark:text-slate-500 text-sm">
        Select a category to see its slides.
      </p>

      <div *ngIf="selectedGroup()" class="overflow-x-auto">
        <table class="w-full text-sm" id="deck_admin_slides_table">
          <thead>
            <tr class="border-b border-slate-100 dark:border-slate-700/50">
              <th class="px-4 py-3 text-left w-10">
                <input
                  type="checkbox"
                  [checked]="isAllSlidesSelected()"
                  (change)="toggleSelectAllSlides($event)"
                  class="w-4 h-4 rounded cursor-pointer accent-purple-600 bg-transparent border border-slate-300 dark:border-slate-700"
                  id="deck_admin_select_all_slides"
                />
              </th>
              <th class="px-4 py-3 text-left text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider w-12">#</th>
              <th class="px-4 py-3 text-left text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Title</th>
              <th class="px-4 py-3 text-left text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider hidden md:table-cell">Key</th>
              <th class="px-4 py-3 text-left text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider hidden lg:table-cell">File</th>
              <th class="px-4 py-3 text-center text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider hidden sm:table-cell">v</th>
              <th class="px-4 py-3 text-center text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Active</th>
              <th class="px-4 py-3 text-right text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngIf="pagesLoading()" class="text-center">
              <td colspan="8" class="py-12 text-slate-400 text-sm">Loading…</td>
            </tr>
            <tr *ngIf="!pagesLoading() && !pages().length" class="text-center">
              <td colspan="8" class="py-12 text-slate-400 dark:text-slate-500 text-sm">
                No slides in this category yet. The AI is never offered an empty category.
              </td>
            </tr>

            <tr
              *ngFor="let slide of paginatedSlides(); trackBy: trackBySlide"
              class="border-b border-slate-50 dark:border-slate-700/30 hover:bg-slate-50/80 dark:hover:bg-slate-700/20 transition-colors"
              [class.opacity-50]="slide.isActive === false"
              [attr.id]="'deck_slide_row_' + slide.key"
            >
              <td class="px-4 py-3.5 w-10">
                <input
                  type="checkbox"
                  [checked]="slideSelection.keys().has(slide.key)"
                  (change)="toggleSelectSlide(slide.key, $event)"
                  (click)="$event.stopPropagation()"
                  class="w-4 h-4 rounded cursor-pointer accent-purple-600 bg-transparent border border-slate-300 dark:border-slate-700"
                  [attr.id]="'deck_select_slide_' + slide.key"
                />
              </td>
              <td class="px-4 py-3.5">
                <span class="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-700/60 text-xs font-bold text-slate-600 dark:text-slate-300">
                  {{ slide.position }}
                </span>
              </td>

              <td class="px-4 py-3.5">
                <p class="font-semibold text-slate-800 dark:text-white text-sm leading-tight">{{ slide.title }}</p>
                <p class="text-[10px] text-slate-400 mt-0.5 line-clamp-1">
                  {{ slide.points.length }} narration point{{ slide.points.length === 1 ? '' : 's' }}
                </p>
              </td>

              <td class="px-4 py-3.5 hidden md:table-cell">
                <code class="text-[10px] bg-slate-100 dark:bg-slate-700/50 text-purple-600 dark:text-purple-400 px-2 py-1 rounded-md font-mono">
                  {{ slide.key }}
                </code>
              </td>

              <td class="px-4 py-3.5 hidden lg:table-cell">
                <span class="text-xs text-slate-500 dark:text-slate-400 font-mono">{{ slide.file }}</span>
                <span *ngIf="slide.htmlBytes" class="ml-1 text-[10px] text-slate-300 dark:text-slate-600">
                  ({{ (slide.htmlBytes / 1024).toFixed(0) }} KB)
                </span>
              </td>

              <td class="px-4 py-3.5 text-center hidden sm:table-cell">
                <span class="text-xs font-semibold text-slate-400 dark:text-slate-500">v{{ slide.version }}</span>
              </td>

              <td class="px-4 py-3.5 text-center">
                <button
                  type="button"
                  (click)="toggleActive(slide)"
                  class="relative w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none"
                  [class.bg-emerald-500]="slide.isActive"
                  [class.bg-slate-200]="!slide.isActive"
                  [class.dark:bg-slate-700]="!slide.isActive"
                  [title]="slide.isActive ? 'Click to hide' : 'Click to show'"
                  [attr.id]="'deck_active_toggle_' + slide.key"
                >
                  <span
                    class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200"
                    [class.translate-x-4]="slide.isActive"
                    [class.translate-x-0]="!slide.isActive"
                  ></span>
                </button>
              </td>

              <td class="px-4 py-3.5">
                <div class="flex items-center justify-end gap-1">
                  <button (click)="openPreview(slide)"
                          class="flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
                          title="Preview slide" [attr.id]="'deck_preview_btn_' + slide.key">
                    <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </button>

                  <button (click)="openSlidePanel('editMeta', slide)"
                          class="flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
                          title="Edit metadata" [attr.id]="'deck_edit_btn_' + slide.key">
                    <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>

                  <button (click)="openSlidePanel('replaceHtml', slide)"
                          class="flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 dark:hover:text-amber-400 transition-colors"
                          title="Replace HTML" [attr.id]="'deck_replace_btn_' + slide.key">
                    <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="16 16 12 12 8 16"></polyline>
                      <line x1="12" y1="12" x2="12" y2="21"></line>
                      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"></path>
                    </svg>
                  </button>

                  <button (click)="confirmDeleteSlide(slide)"
                          class="flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors"
                          title="Delete slide (soft)" [attr.id]="'deck_delete_btn_' + slide.key">
                    <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                      <path d="M10 11v6M14 11v6"></path>
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="p-3 border-t border-slate-100 dark:border-slate-700/50" *ngIf="selectedGroup() && pages().length > 0">
        <app-pagination
          [currentPage]="slidesPager.page()"
          [totalItems]="pages().length"
          [pageSize]="slidesPager.pageSize()"
          [pageSizes]="[5, 10, 15, 20]"
          (pageChange)="slidesPager.setPage($event)"
          (pageSizeChange)="slidesPager.setPageSize($event)"
        ></app-pagination>
      </div>
    </div>
  </div>

  <p class="text-[10px] text-slate-400 dark:text-slate-600 text-center">
    Deletes are soft. Uploading a slide never overwrites an existing one — a duplicate key is
    rejected, and you replace a slide deliberately.
  </p>
</div>

<!-- Environment-move confirmation -->
<div
  *ngIf="envChangeTarget()"
  class="fixed inset-0 z-[9000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
  id="deck_category_env_modal"
>
  <div class="bg-white dark:bg-surface-1 rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4" (click)="$event.stopPropagation()">
    <div class="flex items-center gap-3 mb-4">
      <div class="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
        <svg class="w-5 h-5 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M17 1l4 4-4 4"></path>
          <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
          <path d="M7 23l-4-4 4-4"></path>
          <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
        </svg>
      </div>
      <h3 class="text-sm font-bold text-slate-800 dark:text-white">Move to {{ envLabel(envChangeTarget()!.to) }}?</h3>
    </div>

    <p class="text-xs text-slate-600 dark:text-slate-300 mb-5">
      <strong>{{ envChangeTarget()!.group.title }}</strong> and all of its slides will be visible in
      <strong>{{ envLabel(envChangeTarget()!.to) }}</strong>.
      <span *ngIf="envIncludesProduction(envChangeTarget()!.to)">
        Production live calls can open it immediately.
      </span>
      <span *ngIf="!envIncludesProduction(envChangeTarget()!.to)">
        Production live calls will no longer see it.
      </span>
    </p>

    <div class="flex gap-3">
      <button (click)="cancelGroupEnvChange()"
              class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
              id="deck_category_env_cancel_btn">Cancel</button>
      <button (click)="executeGroupEnvChange()" [disabled]="changingEnv()"
              class="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 transition-colors"
              id="deck_category_env_confirm_btn">
        <span *ngIf="!changingEnv()">Move</span>
        <span *ngIf="changingEnv()" class="flex items-center justify-center gap-2">
          <span class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
          Moving…
        </span>
      </button>
    </div>
  </div>
</div>

<!-- Delete confirmation -->
<app-deck-delete-confirm-modal
  *ngIf="deleteGroupTarget() || deleteSlideTarget() || deleteSelectedSlidesConfirm()"
  [group]="deleteGroupTarget()"
  [slide]="deleteSlideTarget()"
  [bulkCount]="deleteSelectedSlidesConfirm() ? slideSelection.keys().size : 0"
  [deleting]="deleting()"
  (cancel)="cancelDelete()"
  (confirm)="executeDelete()"
></app-deck-delete-confirm-modal>

<!-- Category panel -->
<app-category-form-panel
  *ngIf="categoryPanelOpen()"
  [mode]="categoryPanelMode()"
  [group]="selectedForEdit()"
  [industries]="industries()"
  [nextIndex]="groups().length + 1"
  (saved)="onCategorySaved()"
  (closed)="closeCategoryPanel()"
></app-category-form-panel>

<!-- Slide panel -->
<app-slide-upload-panel
  *ngIf="slidePanelOpen() && selectedGroup()"
  [mode]="slidePanelMode()"
  [groupKey]="selectedGroupKey()!"
  [groupTitle]="selectedGroup()!.title"
  [slide]="selectedSlide()"
  [startPosition]="pages().length + 1"
  (saved)="onSlideSaved()"
  (closed)="closeSlidePanel()"
></app-slide-upload-panel>

<!-- Preview -->
<app-slide-preview-modal
  *ngIf="previewSlide() && selectedGroupKey()"
  [groupKey]="selectedGroupKey()!"
  [pageKey]="previewSlide()!.key"
  [fileName]="previewSlide()!.file"
  [slideTitle]="previewSlide()!.title"
  (close)="previewSlide.set(null)"
></app-slide-preview-modal>
